/**
 * Import validation, CSV <-> record conversion, and JSON export shape.
 * Kept as pure functions (no IndexedDB access) so they're cheap to unit test.
 */
import { RECORD_TYPES, STATUSES, DIMENSIONS, type GvmRecord } from './types';
import { calculateOverallScore } from './scoring';

export const EXPORT_FIELDS: (keyof GvmRecord)[] = [
  'id', 'title', 'description', 'type', 'parent_id', 'category', 'status', 'owner',
  'execution_lane', 'priority_rank', 'execution_allocation_pct',
  'score_vision_alignment', 'score_impact', 'score_revenue_potential', 'score_market_window',
  'score_time_efficiency', 'score_leverage', 'score_evidence_readiness', 'overall_score',
  'next_action', 'activation_gate', 'current_blocker', 'reactivation_trigger',
  'created_at', 'updated_at', 'last_reviewed_at', 'deadline', 'notes', 'archived',
];

export interface ValidationResult {
  valid: GvmRecord[];
  errors: string[];
}

const SCORE_FIELDS = DIMENSIONS.map((d) => `score_${d}` as const);

export function validateImportRows(rows: Array<Record<string, unknown>>): ValidationResult {
  const errors: string[] = [];
  const valid: GvmRecord[] = [];

  rows.forEach((row, i) => {
    const rowErrors: string[] = [];
    const title = String(row.title ?? '').trim();
    if (!title) rowErrors.push(`row ${i + 1}: missing title`);

    const type = String(row.type ?? '');
    if (!RECORD_TYPES.includes(type as any)) {
      rowErrors.push(`row ${i + 1}: invalid type "${type}" (must be one of ${RECORD_TYPES.join(', ')})`);
    }

    const status = row.status ? String(row.status) : 'inbox';
    if (!STATUSES.includes(status as any)) {
      rowErrors.push(`row ${i + 1}: invalid status "${status}" (must be one of ${STATUSES.join(', ')})`);
    }

    for (const field of SCORE_FIELDS) {
      const raw = row[field];
      if (raw === undefined || raw === null || raw === '') continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 1 || n > 10) {
        rowErrors.push(`row ${i + 1}: ${field} must be a number 1-10 (got "${raw}")`);
      }
    }

    if (rowErrors.length) {
      errors.push(...rowErrors);
      return;
    }

    const now = new Date().toISOString();
    const scores = Object.fromEntries(
      DIMENSIONS.map((d) => [d, row[`score_${d}`] != null && row[`score_${d}`] !== '' ? Number(row[`score_${d}`]) : 5]),
    );
    const rec: GvmRecord = {
      id: String(row.id ?? '') || `GVM-IMPORT-${Date.now().toString(36)}-${i}`,
      title,
      description: String(row.description ?? ''),
      type: type as GvmRecord['type'],
      parent_id: row.parent_id ? String(row.parent_id) : null,
      related_ids: Array.isArray(row.related_ids) ? row.related_ids as string[] : [],
      category: String(row.category ?? ''),
      status: status as GvmRecord['status'],
      owner: String(row.owner ?? ''),
      execution_lane: String(row.execution_lane ?? ''),
      priority_rank: row.priority_rank !== undefined && row.priority_rank !== '' ? Number(row.priority_rank) : null,
      execution_allocation_pct: row.execution_allocation_pct !== undefined && row.execution_allocation_pct !== ''
        ? Number(row.execution_allocation_pct) : null,
      score_vision_alignment: scores.vision_alignment,
      score_impact: scores.impact,
      score_revenue_potential: scores.revenue_potential,
      score_market_window: scores.market_window,
      score_time_efficiency: scores.time_efficiency,
      score_leverage: scores.leverage,
      score_evidence_readiness: scores.evidence_readiness,
      overall_score: 0,
      next_action: String(row.next_action ?? ''),
      activation_gate: String(row.activation_gate ?? ''),
      current_blocker: String(row.current_blocker ?? ''),
      reactivation_trigger: String(row.reactivation_trigger ?? ''),
      deadline: row.deadline ? String(row.deadline) : null,
      notes: String(row.notes ?? ''),
      raw_capture: String(row.raw_capture ?? ''),
      created_at: row.created_at ? String(row.created_at) : now,
      updated_at: now,
      last_reviewed_at: row.last_reviewed_at ? String(row.last_reviewed_at) : null,
      archived: row.archived === true || row.archived === 'true' || row.archived === 1 || row.archived === '1',
      deleted_at: null,
    };
    rec.overall_score = calculateOverallScore(scores as any);
    valid.push(rec);
  });

  return { valid, errors };
}

export function recordsToCsv(records: GvmRecord[]): string {
  const header = EXPORT_FIELDS.join(',');
  const lines = records.map((r) =>
    EXPORT_FIELDS.map((f) => csvEscape((r as any)[f])).join(','),
  );
  return [header, ...lines].join('\r\n');
}

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function csvToRows(csv: string): Array<Record<string, string>> {
  const rows = parseCsv(csv);
  if (rows.length === 0) return [];
  const [header, ...body] = rows;
  return body
    .filter((r) => r.length > 1 || (r.length === 1 && r[0] !== ''))
    .map((r) => Object.fromEntries(header.map((h, i) => [h, r[i] ?? ''])));
}

// Minimal RFC4180 CSV parser: handles quoted fields, embedded commas/newlines, and "" escaping.
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const push = () => { row.push(field); field = ''; };
  const pushRow = () => { push(); rows.push(row); row = []; };

  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      field += c; i += 1; continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { push(); i += 1; continue; }
    if (c === '\r') { i += 1; continue; }
    if (c === '\n') { pushRow(); i += 1; continue; }
    field += c; i += 1;
  }
  if (field.length || row.length) pushRow();
  return rows.filter((r) => !(r.length === 1 && r[0] === ''));
}

export function downloadFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
